import React from 'react';

import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { Box, IconButton, InputAdornment, Typography } from '@mui/material';
import { isEmpty } from 'es-toolkit/compat';

import { DeepPartial } from '@hrlogics/helper-types';
import { Field, Form, FormApi, FormSpy } from '@hrlogics/react-components/form';
import { SelectFieldOption } from '@hrlogics/react-helper-types';

import {
  ActivityLogVisibleFor,
  GetActivityLogsListDocument,
  InboxItemType,
  useLeaveCommentForInboxItemMutation,
} from 'src/api';
import { DropzoneFilesField, SelectChipsField, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useInboxItemById, useInboxItemParamId } from 'src/hooks/inboxItem';
import { useCurrentUserPermissions } from 'src/hooks/user';
import type { DroppedFile } from 'src/types/file';
import type { SxProp } from 'src/types/mui';
import { convertFileSize } from 'src/utils/file';

import { FormField } from './constants';
import { FormValues, FormValuesPlatformStaff } from './types';
import { formValidationSchema, formValidationSchemaPlatformStaff } from './validation';

export const InboxItemContentVerificationRequestCreateCommentForm: React.FC = () => {
  const { createApolloContext } = useNotification();

  const { hasPlatformRoles } = useCurrentUserPermissions();

  const inboxItemId = useInboxItemParamId();
  const { inboxItem } = useInboxItemById(inboxItemId);

  const [leaveCommentForInboxItem] = useLeaveCommentForInboxItemMutation({
    context: createApolloContext({
      successMessage: 'New comment has been successfully created',
    }),
    refetchQueries: [
      { query: GetActivityLogsListDocument, variables: { inboxItemId: inboxItemId || '' } },
    ],
  });

  const handleSubmitComment = React.useCallback(
    async (
      formData: FormValues | FormValuesPlatformStaff,
      form: FormApi<FormValues, DeepPartial<FormValues>>,
    ) => {
      if (!inboxItemId || !formData.comment) return;

      try {
        await leaveCommentForInboxItem({
          variables: {
            inboxItemId,
            comment: formData.comment,
            file: formData.files[0]?.file,
            visibleFor: 'visibleFor' in formData ? formData.visibleFor : undefined,
          },
        });

        form.restart();
      } catch (error) {
        console.error(error);
      }
    },
    [inboxItemId, leaveCommentForInboxItem],
  );

  const resolvedFormValidationSchema = React.useMemo(() => {
    if (hasPlatformRoles) {
      return formValidationSchemaPlatformStaff;
    }
    return formValidationSchema;
  }, [hasPlatformRoles]);

  return (
    <Form<FormValues>
      onSubmit={handleSubmitComment}
      initialValues={INITIAL_VALUES}
      subscription={{ valid: true, submitting: true }}
      validationSchema={resolvedFormValidationSchema}
    >
      {({ submitting, valid, handleSubmit, form }) => (
        <React.Fragment>
          {hasPlatformRoles && (
            <Typography variant="body2" color="text.secondary" mb={1}>
              Notes appear on the timeline. Choose which roles can see the note.
            </Typography>
          )}

          <Field
            fullWidth
            name={FormField.comment}
            multiline
            component={TextField}
            showErrorOnlyTouched
            slotProps={{
              input: {
                placeholder: hasPlatformRoles
                  ? 'Type a note for selected roles'
                  : 'Type new comment',
                endAdornment: (
                  <React.Fragment>
                    <InputAdornment position="end">
                      <Field
                        name={FormField.files}
                        component={DropzoneFilesField}
                        disabled={submitting}
                        customChildren={
                          <IconButton>
                            <AttachFileIcon />
                          </IconButton>
                        }
                        hideAttachedFiles
                        immediateUpload={false}
                        maxFiles={1}
                      />
                    </InputAdornment>
                    <InputAdornment position="end">
                      <IconButton
                        color="primary"
                        disabled={!valid || submitting}
                        onClick={handleSubmit}
                      >
                        <SendIcon />
                      </IconButton>
                    </InputAdornment>
                  </React.Fragment>
                ),
              },
            }}
            disabled={submitting}
          />

          {hasPlatformRoles && (
            <Box mt={1}>
              <Field
                name={FormField.visibleFor}
                label="Visible to"
                component={SelectChipsField}
                options={
                  inboxItem?.type === InboxItemType.DataDispute
                    ? DATA_DISPUTE_VISIBLE_FOR_OPTIONS
                    : VISIBLE_FOR_OPTIONS
                }
                size="small"
                disabled={submitting}
                required
              />
            </Box>
          )}

          <FormSpy<FormValues> subscription={{ values: true }}>
            {({ values }) => {
              return (
                <React.Activity mode={!isEmpty(values.files) ? 'visible' : 'hidden'}>
                  <Box mt={2}>
                    {values.files.map(file => {
                      return (
                        <Box mb={1} sx={fileStyles} key={file.id}>
                          <Box sx={fileStyles}>
                            <InsertDriveFileIcon fontSize="medium" color="action" />
                            <Box sx={{ display: 'flex', flexDirection: 'column' }} ml={1}>
                              <Typography variant="body3">
                                <Typography variant="body3" component="span" fontWeight={500}>
                                  {file.uploadedFileName || file.file?.name}
                                </Typography>{' '}
                                {convertFileSize(file.size).toUpperCase()}
                              </Typography>
                            </Box>
                          </Box>

                          <IconButton onClick={() => onDeleteByFileId(form, file.id, values.files)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                </React.Activity>
              );
            }}
          </FormSpy>
        </React.Fragment>
      )}
    </Form>
  );
};

const INITIAL_VALUES = {
  [FormField.files]: [],
} satisfies Partial<FormValues>;

const VISIBLE_FOR_OPTIONS = [
  {
    label: 'Employee',
    value: ActivityLogVisibleFor.Employee,
  },
  {
    label: 'Verifier',
    value: ActivityLogVisibleFor.Verifier,
  },
  {
    label: 'Employer',
    value: ActivityLogVisibleFor.Employer,
  },
  {
    label: 'PEO or Provider',
    value: ActivityLogVisibleFor.PeoOrProvider,
  },
] satisfies SelectFieldOption<ActivityLogVisibleFor>[];

const DATA_DISPUTE_VISIBLE_FOR_OPTIONS = [
  {
    label: 'Employee',
    value: ActivityLogVisibleFor.Employee,
  },
  {
    label: 'Employer',
    value: ActivityLogVisibleFor.Employer,
  },
  {
    label: 'PEO or Provider',
    value: ActivityLogVisibleFor.PeoOrProvider,
  },
] satisfies SelectFieldOption<ActivityLogVisibleFor>[];

const fileStyles: SxProp = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const onDeleteByFileId = (
  form: FormApi<FormValues>,
  fileId: string | undefined | null,
  files: DroppedFile[],
) => {
  form.change(
    FormField.files,
    files?.filter(file => file.id !== fileId),
  );
};
